import React, { useState, useEffect, useCallback, useMemo } from 'react';
import * as api from '../../services/api';
import {
  Database, Plus, RefreshCw, Search, Shield, Lock, Eye, AlertTriangle,
  CheckCircle, Trash2, Edit3, Play, X, Globe, Building2, Filter,
  ToggleLeft, ToggleRight, Copy, FileText, Loader, Zap, BookOpen,
} from 'lucide-react';
import DataClassificationRuleEditor from './DataClassificationRuleEditor';

/* Credo design tokens — mirrored from PolicyConfigPage */
const BRAND = {
  primary: '#0d9488', dark: '#0a766b', light: '#e3f4f1',
  glow: 'rgba(226, 74, 58, 0.06)', mid: '#d4453a',
  soft: '#f8d7d4', tint: '#fff5f4',
};
const S = {
  0: '#ffffff', 25: '#fdfdfc', 50: '#fafaf9', 100: '#f5f5f4', 150: '#eeeded',
  200: '#e7e5e4', 300: '#d6d3d1', 400: '#a8a29e', 500: '#78716c',
  600: '#57534e', 700: '#44403c', 800: '#292524', 900: '#1c1917',
};
const ACCENT = {
  emerald: { bg: '#ecfdf5', text: '#065f46', border: '#a7f3d0', icon: '#10b981' },
  amber:   { bg: '#fffbeb', text: '#92400e', border: '#fde68a', icon: '#f59e0b' },
  sky:     { bg: '#f0f9ff', text: '#0c4a6e', border: '#bae6fd', icon: '#0ea5e9' },
  rose:    { bg: '#fff1f2', text: '#9f1239', border: '#fecdd3', icon: '#f43f5e' },
  violet:  { bg: '#f5f3ff', text: '#5b21b6', border: '#ddd6fe', icon: '#8b5cf6' },
  slate:   { bg: '#f8fafc', text: '#475569', border: '#e2e8f0', icon: '#64748b' },
};

// Category visual config — matches what V59 seeds and DataProtectionGate scans for
const CATEGORY_CONFIG = {
  PII:        { Icon: Eye,       accent: ACCENT.violet,  label: 'PII',         desc: 'Personally identifiable information' },
  CREDENTIAL: { Icon: Lock,      accent: ACCENT.rose,    label: 'Credential',  desc: 'API keys, tokens, passwords' },
  FINANCIAL:  { Icon: Database,  accent: ACCENT.amber,   label: 'Financial',   desc: 'Card numbers, account data' },
  HEALTH:     { Icon: Shield,    accent: ACCENT.sky,     label: 'Health',      desc: 'PHI, medical records' },
  CORPORATE:  { Icon: Building2, accent: ACCENT.emerald, label: 'Corporate',   desc: 'Trade secrets, internal data' },
  CUSTOM:     { Icon: Zap,       accent: ACCENT.slate,   label: 'Custom',      desc: 'Tenant-defined rules' },
};

const ACTION_CONFIG = {
  BLOCK:    { color: '#dc2626', bg: '#fef2f2', border: '#fecaca', label: 'Block',    desc: 'Reject the entire scan; nothing flows downstream' },
  REDACT:   { color: '#9f1239', bg: '#fff1f2', border: '#fecdd3', label: 'Redact',   desc: 'Replace with [REDACTED:category]' },
  TOKENIZE: { color: '#5b21b6', bg: '#f5f3ff', border: '#ddd6fe', label: 'Tokenize', desc: 'Swap for vault token (reversible)' },
  MASK:     { color: '#92400e', bg: '#fffbeb', border: '#fde68a', label: 'Mask',     desc: 'Partial mask (e.g. ****1234)' },
  LOG:      { color: '#475569', bg: '#f8fafc', border: '#e2e8f0', label: 'Log only', desc: 'Detect & audit, do not modify' },
};

const SCOPE_CONFIG = {
  SYSTEM: { Icon: Globe,     label: 'System default', color: ACCENT.slate.icon, bg: ACCENT.slate.bg, border: ACCENT.slate.border, text: ACCENT.slate.text },
  TENANT: { Icon: Building2, label: 'Tenant',         color: BRAND.primary,     bg: BRAND.light,     border: BRAND.soft,           text: BRAND.dark },
  ORG:    { Icon: Shield,    label: 'Org override',   color: ACCENT.violet.icon,bg: ACCENT.violet.bg,border: ACCENT.violet.border, text: ACCENT.violet.text },
};

// Hardcoded canonical id from SystemIds.SYSTEM_TENANT_ID (Java source of truth)
const SYSTEM_TENANT_ID = '00000000-0000-0000-0000-000000000011';

const inferScope = (rule) => {
  if (rule.tenantId === SYSTEM_TENANT_ID) return 'SYSTEM';
  if (rule.orgId) return 'ORG';
  return 'TENANT';
};

const inputBase = {
  width: '100%', padding: '9px 12px', border: `1px solid ${S[200]}`, borderRadius: 8,
  fontSize: 13, fontFamily: 'inherit', background: '#fff', color: S[900],
  outline: 'none', transition: 'border-color 0.15s, box-shadow 0.15s',
  boxSizing: 'border-box',
};
const btnBase = {
  border: 'none', cursor: 'pointer', borderRadius: 8, fontFamily: 'inherit',
  display: 'inline-flex', alignItems: 'center', gap: 6, transition: 'all 0.15s ease',
};

/* ═══════════════════════════════════════════════════════════════════ */
export default function DataClassificationRulesPanel() {
  const [rules, setRules] = useState([]);
  const [stats, setStats] = useState({ total: 0, enabled: 0, blocking: 0, byCategory: {} });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | rule object | 'new'
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('ALL');
  const [actionFilter, setActionFilter] = useState('ALL');
  const [scopeFilter, setScopeFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [rulesRes, statsRes] = await Promise.all([
        api.listDpgRules(),
        api.getDpgRuleStats(),
      ]);
      if (rulesRes.ok) setRules(rulesRes.data || []);
      else setError(rulesRes.data?.message || 'Failed to load rules');
      if (statsRes.ok) setStats(statsRes.data || {});
    } catch (e) {
      console.error(e);
      setError(e.message || 'Failed to load rules');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleToggle = async (rule) => {
    try {
      const res = await api.toggleDpgRule(rule.id, !rule.enabled);
      if (res.ok) await load();
      else alert(res.data?.message || 'Toggle failed');
    } catch (e) { alert(e.message); }
  };

  const handleDelete = async (rule) => {
    if (!window.confirm(`Delete rule "${rule.name}"? This cannot be undone.`)) return;
    try {
      const res = await api.deleteDpgRule(rule.id);
      if (res.ok) await load();
      else alert(res.data?.message || 'Delete failed');
    } catch (e) { alert(e.message); }
  };

  const handleSave = async (payload, isCreate) => {
    try {
      const res = isCreate
        ? await api.createDpgRule(payload)
        : await api.updateDpgRule(editing.id, payload);
      if (res.ok) {
        setEditing(null);
        await load();
      } else {
        alert(res.data?.message || 'Save failed');
      }
    } catch (e) { alert(e.message); }
  };

  const filtered = useMemo(() => {
    return rules.filter(r => {
      if (categoryFilter !== 'ALL' && r.category !== categoryFilter) return false;
      if (actionFilter !== 'ALL' && r.action !== actionFilter) return false;
      if (scopeFilter !== 'ALL' && inferScope(r) !== scopeFilter) return false;
      if (statusFilter === 'ENABLED' && !r.enabled) return false;
      if (statusFilter === 'DISABLED' && r.enabled) return false;
      if (search) {
        const q = search.toLowerCase();
        return (r.name || '').toLowerCase().includes(q)
            || (r.description || '').toLowerCase().includes(q)
            || (r.complianceFramework || '').toLowerCase().includes(q);
      }
      return true;
    });
  }, [rules, categoryFilter, actionFilter, scopeFilter, statusFilter, search]);

  return (
    <div style={{ animation: 'pcpFadeIn 0.3s ease' }}>
      {/* ═══ Header explainer ═══ */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(226,74,58,0.04) 0%, rgba(197,58,43,0.02) 100%)',
        border: `1px solid ${BRAND.soft}`, borderRadius: 12, padding: '16px 20px', marginBottom: 20,
        display: 'flex', alignItems: 'flex-start', gap: 12,
      }}>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: '#fff', border: `1px solid ${BRAND.soft}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Shield size={18} color={BRAND.primary} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: S[900], marginBottom: 4 }}>
            Data Protection Gate — Classification Rules
          </div>
          <div style={{ fontSize: 12, color: S[600], lineHeight: 1.55 }}>
            Rules below are evaluated by every DPG node in every workflow this tenant runs.
            On match, the configured action fires (block / redact / tokenize / mask / log) before
            sensitive data reaches an LLM, an external system, or another node. System defaults are
            shipped by the platform; tenant rules override them per-priority.
          </div>
        </div>
      </div>

      {/* ═══ Stats cards ═══ */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 20 }}>
        {[
          { label: 'Total Rules',     value: stats.total ?? 0,    sub: `${rules.length} visible`,                   accent: ACCENT.sky,     Icon: BookOpen },
          { label: 'Enabled',         value: stats.enabled ?? 0,  sub: `${(stats.total ?? 0) - (stats.enabled ?? 0)} disabled`, accent: ACCENT.emerald, Icon: CheckCircle },
          { label: 'Blocking Rules',  value: stats.blocking ?? 0, sub: 'Will reject scan on match',                  accent: ACCENT.rose,    Icon: AlertTriangle },
          { label: 'Categories Used', value: Object.keys(stats.byCategory || {}).length, sub: 'of 6 available',     accent: ACCENT.violet,  Icon: Database },
        ].map((s, i) => (
          <div key={s.label} style={{
            background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 12,
            padding: '18px 20px', position: 'relative', overflow: 'hidden',
            animation: 'pcpSlideUp 0.3s ease', animationDelay: `${i * 0.06}s`, animationFillMode: 'backwards',
          }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: s.accent.icon, opacity: 0.15 }} />
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: s.accent.text, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>{s.label}</div>
                <div style={{ fontSize: 28, fontWeight: 800, color: S[900], lineHeight: 1, fontFeatureSettings: "'tnum'" }}>{s.value}</div>
                <div style={{ fontSize: 11, color: S[500], marginTop: 6 }}>{s.sub}</div>
              </div>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: s.accent.bg, border: `1px solid ${s.accent.border}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <s.Icon size={18} style={{ color: s.accent.icon }} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ═══ Category distribution strip ═══ */}
      {Object.keys(stats.byCategory || {}).length > 0 && (
        <div style={{
          background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 12,
          padding: '14px 20px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap',
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: S[600], textTransform: 'uppercase', letterSpacing: '0.08em' }}>By category</div>
          {Object.entries(stats.byCategory).map(([cat, count]) => {
            const cfg = CATEGORY_CONFIG[cat] || CATEGORY_CONFIG.CUSTOM;
            return (
              <div key={cat} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 10px',
                borderRadius: 999, background: cfg.accent.bg, border: `1px solid ${cfg.accent.border}` }}>
                <cfg.Icon size={13} color={cfg.accent.icon} />
                <span style={{ fontSize: 12, fontWeight: 600, color: cfg.accent.text }}>{cfg.label}</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: S[900], fontFeatureSettings: "'tnum'" }}>{count}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* ═══ Filter bar ═══ */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '1 1 280px', minWidth: 240 }}>
          <Search size={15} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: S[400] }} />
          <input className="pcp-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, description, or compliance framework…"
            style={{ ...inputBase, paddingLeft: 36 }}
          />
        </div>
        <FilterSelect label="Category" value={categoryFilter} onChange={setCategoryFilter}
          options={[{ v: 'ALL', l: 'All categories' }, ...Object.entries(CATEGORY_CONFIG).map(([k, c]) => ({ v: k, l: c.label }))]}
        />
        <FilterSelect label="Action" value={actionFilter} onChange={setActionFilter}
          options={[{ v: 'ALL', l: 'All actions' }, ...Object.entries(ACTION_CONFIG).map(([k, c]) => ({ v: k, l: c.label }))]}
        />
        <FilterSelect label="Scope" value={scopeFilter} onChange={setScopeFilter}
          options={[
            { v: 'ALL', l: 'All scopes' },
            { v: 'SYSTEM', l: 'System defaults' },
            { v: 'TENANT', l: 'Tenant-wide' },
            { v: 'ORG', l: 'Org overrides' },
          ]}
        />
        <FilterSelect label="Status" value={statusFilter} onChange={setStatusFilter}
          options={[
            { v: 'ALL', l: 'All statuses' },
            { v: 'ENABLED', l: 'Enabled only' },
            { v: 'DISABLED', l: 'Disabled only' },
          ]}
        />
        <button onClick={load} disabled={loading}
          style={{ ...btnBase, background: '#fff', border: `1px solid ${S[200]}`, color: S[700], padding: '8px 12px', fontSize: 13 }}>
          <RefreshCw size={14} style={{ animation: loading ? 'pcpSpin 0.8s linear infinite' : 'none' }} />
          Refresh
        </button>
        <button onClick={() => setEditing('new')}
          style={{ ...btnBase, background: BRAND.primary, color: '#fff', padding: '8px 14px', fontSize: 13, fontWeight: 600 }}>
          <Plus size={14} /> New Rule
        </button>
      </div>

      {error && (
        <div style={{ marginBottom: 16, padding: '10px 14px', borderRadius: 10,
          background: ACCENT.rose.bg, border: `1px solid ${ACCENT.rose.border}`, color: ACCENT.rose.text, fontSize: 13 }}>
          {error}
        </div>
      )}

      {/* ═══ Rules table ═══ */}
      <div style={{ background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 12, overflow: 'hidden' }}>
        <div style={{
          display: 'grid', gridTemplateColumns: '2.2fr 0.9fr 0.9fr 0.7fr 0.9fr 0.6fr 0.6fr',
          gap: 12, padding: '11px 16px', background: S[50], borderBottom: `1px solid ${S[200]}`,
          fontSize: 10, fontWeight: 700, color: S[600], textTransform: 'uppercase', letterSpacing: '0.06em',
        }}>
          <div>Rule</div>
          <div>Category</div>
          <div>Detection</div>
          <div>Action</div>
          <div>Scope</div>
          <div>Sensitivity</div>
          <div style={{ textAlign: 'right' }}>Actions</div>
        </div>
        {loading ? (
          <div style={{ padding: '60px 20px', textAlign: 'center', color: S[500] }}>
            <Loader size={24} style={{ animation: 'pcpSpin 0.8s linear infinite' }} />
            <div style={{ marginTop: 12, fontSize: 13 }}>Loading rules…</div>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: '60px 20px', textAlign: 'center' }}>
            <Shield size={32} style={{ color: S[300] }} />
            <div style={{ marginTop: 12, fontSize: 14, fontWeight: 600, color: S[600] }}>No classification rules match your filters</div>
            <div style={{ fontSize: 12, color: S[500], marginTop: 4 }}>
              {rules.length === 0 ? 'Get started by creating your first rule.' : 'Try clearing the filters above.'}
            </div>
            {rules.length === 0 && (
              <button onClick={() => setEditing('new')}
                style={{ ...btnBase, marginTop: 16, background: BRAND.primary, color: '#fff', padding: '9px 16px', fontSize: 13, fontWeight: 600 }}>
                <Plus size={14} /> Create your first rule
              </button>
            )}
          </div>
        ) : filtered.map(rule => (
          <RuleRow
            key={rule.id}
            rule={rule}
            onEdit={() => setEditing(rule)}
            onToggle={() => handleToggle(rule)}
            onDelete={() => handleDelete(rule)}
          />
        ))}
      </div>

      {editing && (
        <DataClassificationRuleEditor
          rule={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSave={(payload) => handleSave(payload, editing === 'new')}
        />
      )}
    </div>
  );
}

/* ─── RuleRow ─────────────────────────────────────────────────────── */
function RuleRow({ rule, onEdit, onToggle, onDelete }) {
  const cat = CATEGORY_CONFIG[rule.category] || CATEGORY_CONFIG.CUSTOM;
  const act = ACTION_CONFIG[rule.action] || ACTION_CONFIG.LOG;
  const scope = inferScope(rule);
  const sc = SCOPE_CONFIG[scope];
  const isSystem = scope === 'SYSTEM';

  return (
    <div className="pcp-row" style={{
      display: 'grid', gridTemplateColumns: '2.2fr 0.9fr 0.9fr 0.7fr 0.9fr 0.6fr 0.6fr',
      gap: 12, padding: '13px 16px', borderBottom: `1px solid ${S[150]}`, alignItems: 'center',
      opacity: rule.enabled ? 1 : 0.55,
    }}>
      {/* Rule name + description */}
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: S[900], display: 'flex', alignItems: 'center', gap: 6 }}>
          {rule.name}
          {!rule.enabled && (
            <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: S[150], color: S[600], fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Off</span>
          )}
        </div>
        {rule.description && (
          <div style={{ fontSize: 11, color: S[500], marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {rule.description}
          </div>
        )}
        {rule.complianceFramework && (
          <div style={{ fontSize: 10, color: ACCENT.sky.text, marginTop: 4, fontWeight: 600 }}>
            {rule.complianceFramework}
          </div>
        )}
      </div>

      {/* Category */}
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 8px',
        background: cat.accent.bg, border: `1px solid ${cat.accent.border}`, borderRadius: 6, width: 'fit-content' }}>
        <cat.Icon size={12} color={cat.accent.icon} />
        <span style={{ fontSize: 11, fontWeight: 600, color: cat.accent.text }}>{cat.label}</span>
      </div>

      {/* Detection method */}
      <div style={{ fontSize: 12, color: S[700], fontFamily: 'ui-monospace, SFMono-Regular, monospace' }}>
        {rule.detectionMethod}
      </div>

      {/* Action */}
      <div style={{ display: 'inline-flex', alignItems: 'center', padding: '3px 8px',
        background: act.bg, border: `1px solid ${act.border}`, borderRadius: 6, width: 'fit-content' }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: act.color, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{act.label}</span>
      </div>

      {/* Scope */}
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 8px',
        background: sc.bg, border: `1px solid ${sc.border}`, borderRadius: 6, width: 'fit-content' }}>
        <sc.Icon size={11} color={sc.color} />
        <span style={{ fontSize: 11, fontWeight: 600, color: sc.text }}>{sc.label}</span>
      </div>

      {/* Sensitivity */}
      <SensitivityBar level={rule.sensitivityLevel} />

      {/* Actions */}
      <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
        <IconBtn title={rule.enabled ? 'Disable' : 'Enable'} onClick={onToggle}>
          {rule.enabled ? <ToggleRight size={16} color={ACCENT.emerald.icon} /> : <ToggleLeft size={16} color={S[400]} />}
        </IconBtn>
        <IconBtn title="Edit" onClick={onEdit}><Edit3 size={14} /></IconBtn>
        {!isSystem && (
          <IconBtn title="Delete" onClick={onDelete} danger><Trash2 size={14} /></IconBtn>
        )}
      </div>
    </div>
  );
}

function SensitivityBar({ level }) {
  const lvl = Math.min(5, Math.max(1, level || 1));
  const colors = ['#84cc16', '#a3e635', '#facc15', '#fb923c', '#dc2626'];
  return (
    <div style={{ display: 'flex', gap: 2, alignItems: 'center' }} title={`Sensitivity ${lvl} of 5`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} style={{
          width: 6, height: 14, borderRadius: 2,
          background: i < lvl ? colors[lvl - 1] : S[150],
        }} />
      ))}
      <span style={{ fontSize: 11, fontWeight: 700, color: S[700], marginLeft: 6, fontFeatureSettings: "'tnum'" }}>{lvl}</span>
    </div>
  );
}

function IconBtn({ children, title, onClick, danger }) {
  return (
    <button onClick={onClick} title={title} className="pcp-action-btn"
      style={{ ...btnBase, padding: '5px 6px', background: 'transparent',
        color: danger ? ACCENT.rose.icon : S[600] }}>
      {children}
    </button>
  );
}

function FilterSelect({ label, value, onChange, options }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} title={label}
      style={{ ...inputBase, width: 'auto', minWidth: 130, padding: '8px 10px', fontSize: 12 }}>
      {options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
    </select>
  );
}
